// shell/touch-guards.ts -- kill browser touch "conveniences" that break
// frantic game input: pinch/smart zoom, double-tap zoom on taps that land
// between the control buttons, long-press magnifier. The CSS side
// (touch-action on the game roots + viewport user-scalable=no) covers
// spec-compliant browsers (Android Chrome, Windows Edge); iOS Safari ignores
// user-scalable=no since iOS 10 and needs these JS belts-and-braces.
//
// MapLibre maps (browse tracks, charge) keep their own touch handling: they
// consume touch events directly, so preventing the BROWSER defaults here
// never reaches them -- and their containers are excluded from the
// double-tap suppression anyway.

/** Elements whose native double-tap/double-click behavior stays intact. */
const INTERACTIVE =
  "button, a, input, select, textarea, label, .maplibregl-map, .tdice-arena--interactive";

export function installTouchGuards(doc: Document = document): void {
  const stop = (e: Event) => e.preventDefault();
  // iOS pinch zoom fires non-standard gesture events (ignored elsewhere).
  // Preventing them blocks Safari's page zoom without touching the games'
  // own touch handling.
  doc.addEventListener("gesturestart", stop);
  doc.addEventListener("gesturechange", stop);
  doc.addEventListener("gestureend", stop);
  // iOS double-tap smart zoom: suppress the browser default on the second
  // of two quick taps, but ONLY on non-interactive targets (canvas, HUD
  // gaps, missed button presses) so click synthesis on buttons/links and
  // text selection in inputs keep working.
  let lastTouchEnd = 0;
  doc.addEventListener("touchend", (e) => {
    const now = Date.now();
    const interactive = (e.target as Element | null)?.closest?.(INTERACTIVE);
    if (!interactive && now - lastTouchEnd < 350 && e.cancelable) {
      e.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });
  // Windows touch double-tap zoom arrives as a dblclick default.
  doc.addEventListener("dblclick", (e) => {
    if (!(e.target as Element | null)?.closest?.(INTERACTIVE)) e.preventDefault();
  });
}
